import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Asset, AssetPath } from '@lemontaps/lib/types/api/asset.type';
import { ASSET_PATHS } from '@lemontaps/lib/constants';
import { uploadAsset as uploadAssetApi } from '../apis/assets';

export default function useAssets() {
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  const uploadAsset = useCallback(
    async (
      file: File,
      path: AssetPath = ASSET_PATHS.Font,
      references: { themeConfigReference?: string; jsonColReference?: string } = {},
      onUploadProgress?: (progress: number) => void,
    ): Promise<Asset> => {
      return await uploadAssetApi(getAccessTokenSilently, file, path, references, onUploadProgress);
    },
    [getAccessTokenSilently],
  );

  const allocateAsset = useCallback(
    async (
      file: File,
      path: AssetPath = ASSET_PATHS.Font,
      references: { themeConfigReference?: string; jsonColReference?: string } = {},
      onUploadProgress?: (progress: number) => void,
    ): Promise<Asset> => {
      setLoading(true);
      try {
        return await uploadAsset(file, path, references, onUploadProgress);
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [uploadAsset],
  );

  const allocateAssets = useCallback(
    async (
      files: File[],
      maxConcurrent: number = 3,
      path: AssetPath = ASSET_PATHS.Font,
      references: { themeConfigReference?: string; jsonColReference?: string } = {},
      onUploadProgress?: (progress: number) => void,
    ): Promise<Asset[]> => {
      setLoading(true);
      try {
        const batchSize = Math.min(maxConcurrent, files.length);
        const iterations = Math.ceil(files.length / batchSize);
        const assets: Asset[] = [];

        let totalMinProgress = 0;
        for (let i = 0; i < iterations; i++) {
          const batchFiles = files.slice(i * batchSize, (i + 1) * batchSize);
          const localProgresses: number[] = Array(batchFiles.length).fill(0);
          const individualProgressHandler = (progress: number, index: number) => {
            localProgresses[index] = progress;
            const totalProgress =
              totalMinProgress +
              localProgresses.reduce((acc, curr) => acc + curr, 0) / files.length;
            onUploadProgress?.(totalProgress);
          };
          const batchPromises = batchFiles.map((file, idx) =>
            uploadAsset(file, path, references, p => individualProgressHandler(p, idx)),
          );
          const batchResults = await Promise.all(batchPromises);
          assets.push(...batchResults);
          totalMinProgress += batchFiles.length / files.length;
        }

        return assets;
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [uploadAsset],
  );

  return {
    allocateAsset,
    allocateAssets,
    loading,
  };
}
